import currencies from '../models/Currencies';
import { getBalticIndexByValue } from '../models/BalticRateIndices';
import { ENTITY_PART_TYPE_RATE } from '_legacy/models/common/EntityPart';
import { flatRateValue, sparkIndexValue } from '../models/RateTypes';
import { getSparkIndexByValue } from '../models/SparkRateIndices';
import { DATASET_TANKERS } from '_legacy/models/Datasets';
import { SHORT_HAND_RATE_FORMAT } from '../services/RateFormatServiceFactory';
import { mapHyphenSeparator } from '_legacy/services/hyphenSeparatorService';

class RateFormatter {
    numberFormat = new Intl.NumberFormat('en-GB');

    numberFormatForDecimalValues = new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: 2,
    });

    startValueForHidingDecimals = 1000;

    format(part, datasetId, rateFormat) {
        if (!part) {
            return '';
        }
        if (part.partType === ENTITY_PART_TYPE_RATE) {
            const formattedDuration = part.rateValue.duration
                ? this.formatDuration(part.rateValue.duration)
                : '';

            const formattedRateValue =
                part.rateValue.type === flatRateValue
                    ? this.formatFlatRate(
                          part.rateValue.flatRate,
                          datasetId,
                          rateFormat
                      )
                    : part.rateValue.type === sparkIndexValue
                    ? this.formatSparkIndex(part.rateValue.sparkIndex)
                    : this.formatBalticIndex(part.rateValue.balticIndex);

            return formattedDuration === ''
                ? `${formattedRateValue}`
                : `${formattedRateValue} ${formattedDuration}`;
        } else {
            return mapHyphenSeparator(part.separatorValue);
        }
    }

    formatFlatRate(flatRate, datasetId, rateFormat) {
        if (flatRate.amount && flatRate.amount.from && flatRate.amount.to) {
            const amountString = this.formatAmount(flatRate.amount);

            if (flatRate.unit === 'WS' && datasetId === DATASET_TANKERS) {
                if (rateFormat === SHORT_HAND_RATE_FORMAT) {
                    return `W${this.formatAmountForWS(flatRate.amount)}`;
                }
                return `W${amountString}`;
            }
            if (flatRate.currency && flatRate.currency !== 'Unknown') {
                const boundString = flatRate.bound ? `${flatRate.bound} ` : '';

                const currency = currencies.find(
                    (ccy) => ccy.value === flatRate.currency
                )?.label;

                if (
                    flatRate.unit === 'LS' &&
                    rateFormat === SHORT_HAND_RATE_FORMAT
                ) {
                    const amountShortHand = this.formatAmountShortHand(
                        flatRate.amount
                    );
                    let rateString = `${boundString}${currency}${amountShortHand}`;
                    if (flatRate.info) {
                        rateString += ` ${flatRate.info}`;
                    }
                    if (flatRate.isEquiv) {
                        rateString += ' (equiv)';
                    }
                    return rateString;
                }

                let rateString = `${boundString}${currency}${amountString} ${flatRate.unit}`;

                if (flatRate.unit === 'PMT' && flatRate.balticRoute) {
                    rateString += ` ${flatRate.balticRoute}`;
                }

                if (flatRate.info) {
                    rateString += ` ${flatRate.info}`;
                }

                if (flatRate.isEquiv) {
                    rateString += ' (equiv)';
                }

                return rateString;
            }

            let rateString = `${flatRate.unit} ${amountString}`;

            if (flatRate.unit === 'PMT' && flatRate.balticRoute) {
                rateString = `${amountString} ${flatRate.unit} ${flatRate.balticRoute}`;
            }

            if (flatRate.info) {
                rateString += ` ${flatRate.info}`;
            }

            if (flatRate.isEquiv) {
                rateString += ' (equiv)';
            }

            return rateString;
        }

        return flatRate.unit;
    }

    formatAmountShortHand(amount) {
        if (!amount) return '';

        return amount.from === amount.to
            ? this.formatNumberShortHand(amount.from)
            : `${this.formatNumberShortHand(
                  amount.from
              )}-${this.formatNumberShortHand(amount.to)}`;
    }

    formatNumberShortHand(value) {
        if (isNaN(value)) return '';

        if (value >= 1_000_000) {
            return `${parseFloat((value / 1_000_000).toFixed(3))}M`;
        }
        if (value >= 1_000) {
            const thousands = parseFloat((value / 1_000).toFixed(3));
            // toFixed(3) can round up across the 1000K → 1M boundary (e.g. 999,999.9 → "1000K").
            // Re-classify to M when that happens so the suffix stays consistent.
            if (thousands >= 1_000) {
                return `${parseFloat((value / 1_000_000).toFixed(3))}M`;
            }
            return `${thousands}K`;
        }
        return String(value);
    }

    formatAmountForWS(amount) {
        if (!amount) return '';

        return amount.from === amount.to
            ? this.formatNumberForWS(amount.from)
            : `${this.formatNumberForWS(amount.from)}-${this.formatNumberForWS(
                  amount.to
              )}`;
    }

    formatNumberForWS(value) {
        if (isNaN(value)) return '';

        // Trim trailing zeros to at most 2 decimal places
        const trimmed = parseFloat(value.toFixed(2));
        if (trimmed >= this.startValueForHidingDecimals) {
            return this.numberFormat.format(trimmed);
        }
        return trimmed.toString();
    }

    formatBalticIndex(balticIndex) {
        const amountString = this.formatAmount(balticIndex.amount, true);
        const rateIndex = getBalticIndexByValue(balticIndex.index);

        return `${amountString}% ${rateIndex?.label}`;
    }

    formatSparkIndex(sparkIndex) {
        const amountString = this.formatAmount(sparkIndex.amount, true);
        const rateIndex = getSparkIndexByValue(sparkIndex.index);

        return `${amountString} ${rateIndex?.label}`;
    }

    formatAmount(amount, withoutNumberFormatting) {
        if (!amount) return '';

        return amount.from === amount.to
            ? this.formatNumber(amount.from, withoutNumberFormatting)
            : `${this.formatNumber(
                  amount.from,
                  withoutNumberFormatting
              )}-${this.formatNumber(amount.to, withoutNumberFormatting)}`;
    }

    formatDuration(duration) {
        const { amount, unit } = duration;

        const formattedAmount = this.formatAmount(amount, true);
        const formattedAmountEnding = this.formatAmountEnding(amount);
        const formattedUnit =
            amount.from === amount.to ? unit.slice(0, -1) : unit;

        return `(${formattedAmount}${formattedAmountEnding} ${formattedUnit})`;
    }

    formatAmountEnding(amount) {
        if (!amount.from || amount.from !== amount.to) {
            return '';
        }

        const tenths = amount.from % 10;
        const hundredths = amount.from % 100;

        return tenths === 1 && hundredths !== 11
            ? 'ST'
            : tenths === 2 && hundredths !== 12
            ? 'ND'
            : tenths === 3 && hundredths !== 13
            ? 'RD'
            : 'TH';
    }

    formatNumber(value, withoutNumberFormatting) {
        if (isNaN(value)) {
            return '';
        }

        return withoutNumberFormatting
            ? value
            : value >= this.startValueForHidingDecimals
            ? this.numberFormat.format(value)
            : this.numberFormatForDecimalValues.format(value);
    }
}

const rateFormatterInstance = new RateFormatter();

export { RateFormatter, rateFormatterInstance };
